import { Fragment, MouseEvent, useEffect, useState } from 'react'
import { isNil } from 'lodash-es'
import { Menu, StrictTabProps } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'

import { Text, PopupMenu, Anchor } from '../'
import {
  TabContainer,
  StyledTab,
  TabItem,
  PopupMenuTrigger,
  PopupMenuIcon,
  AddTabIcon,
  TabWrapper,
} from './Styles'
import { ActivePane, Pane } from './types'
import { CSSProperties } from 'styled-components'

interface Props extends Omit<StrictTabProps, 'panes'> {
  panes: Pane[]
  noMargin?: boolean
  controlledIndex?: number
  style?: CSSProperties
  sticky?: boolean
  onChange?: (e: MouseEvent, { activeIndex, panes }: ActivePane) => void
  onAddTab?: () => void | undefined
  className?: string
}

const CustomTab = ({
  panes,
  noMargin,
  controlledIndex,
  style,
  sticky,
  onChange,
  onAddTab,
  className,
  ...rest
}: Props) => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!isNil(controlledIndex)) {
      setActiveIndex(controlledIndex)
    }
  }, [controlledIndex])

  const Page = panes[activeIndex]?.render
    ? panes?.[activeIndex]?.render?.()
    : null

  const updateActiveIndex = (e: MouseEvent, i: number) => {
    setActiveIndex(i)
    if (onChange) {
      onChange(e, { activeIndex: i, panes })
    }
  }

  return (
    <Fragment>
      <TabContainer noMargin={noMargin} sticky={sticky} style={style}>
        {panes.length === 0 && (
          <Anchor
            onClick={onAddTab}
            title={t('Manage tabs')}
            style={{ height: '50px' }}>
            <AddTabIcon />
          </Anchor>
        )}
        <StyledTab
          {...rest}
          className={className}
          menu={{
            secondary: true,
            pointing: true,
          }}
          panes={panes.map(
            ({ render, menuItem, notify, disabled, ...pane }: Pane, i) => ({
              index: i,
              menuItem: (
                <Fragment key={i}>
                  <Menu.Item
                    disabled={disabled}
                    active={activeIndex === i}
                    onClick={e => updateActiveIndex(e, i)}>
                    <TabItem notify={notify}>
                      <Text.Span light={activeIndex !== i}>
                        {menuItem}
                      </Text.Span>
                    </TabItem>
                  </Menu.Item>

                  {onAddTab && panes.length === i + 1 && (
                    <Anchor onClick={onAddTab} title={t('Manage tabs')}>
                      <AddTabIcon />
                    </Anchor>
                  )}
                </Fragment>
              ),
              ...pane,
            }),
          )}
        />
        <PopupMenu
          basic
          position="bottom left"
          active={activeIndex}
          items={panes.map(({ menuItem }, i) => ({
            title: menuItem,
            onClick: (e: MouseEvent) => updateActiveIndex(e, i),
          }))}
          customTrigger={
            <PopupMenuTrigger>
              <PopupMenuIcon />
            </PopupMenuTrigger>
          }
        />
      </TabContainer>
      {Page}
    </Fragment>
  )
}

// Used to wrap tabs in modals so they get it get the correct margins
CustomTab.Wrapper = TabWrapper

export default CustomTab
